import type { ContractError } from '../api';
import type { LanguageChoice } from '../i18n/languages';

/** The person a session belongs to, as the server describes them. */
export interface SessionUser {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly isStaff: boolean;
}

/** What the server keeps about the signed-in person beyond who they are. */
export interface PersonDetails {
  readonly number: string | null;
  readonly language: LanguageChoice;
  readonly phoneVerified: boolean;
  readonly emailVerified: boolean;
  /** Unknown when an older server leaves it out. */
  readonly passwordSet: boolean | undefined;
  readonly pictureUrl: string | null;
  readonly hasDashboardChoice: boolean;
}

/**
 * What this tab knows of the person beyond who they are. Sign-in names only who they are; the server's own
 * read describes them. A description that cannot be read fails only the screens that show it — never sign-in.
 */
export type DetailsState =
  | { readonly status: 'unread' }
  | { readonly status: 'read'; readonly value: PersonDetails }
  | { readonly status: 'unreadable'; readonly error: ContractError };

export const DETAILS_UNREAD: DetailsState = { status: 'unread' };

export interface ViewingAs {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly isStaff: boolean;
}

export interface ViewablePerson {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
}

export interface ViewAsSwitch {
  readonly landingPage: string | null;
}

export interface SignedInSession {
  readonly status: 'signedIn';
  readonly user: SessionUser;
  readonly details: DetailsState;
  readonly viewingAs: ViewingAs | null;
  readonly landingPage: string | null;
}

export type SignedOutReason = 'noSession' | 'loggedOut';

export interface SignedOutSession {
  readonly status: 'signedOut';
  readonly reason: SignedOutReason;
}

/** What the server says about this browser's session. The browser keeps no copy of its own. */
export type ServerSession = SignedInSession | SignedOutSession;

export const SIGNED_OUT: SignedOutSession = { status: 'signedOut', reason: 'noSession' };

export const LOGGED_OUT: SignedOutSession = { status: 'signedOut', reason: 'loggedOut' };
